/* ============================================================
   Format Explainer — Scroll-driven camera choreography
   Supports embedded (product page) and standalone modes.
   ============================================================ */
(function () {
  'use strict';

  var page = document.getElementById('FormatExplainer');
  if (!page) return;

  var isEmbedded = page.getAttribute('data-fe-embedded') === 'true';
  var nav = document.getElementById('FENav');
  var indicator = document.getElementById('FENavIndicator');
  var chapters = page.querySelectorAll('.fe-chapter');
  var navTabs = nav ? nav.querySelectorAll('.fe-nav__tab') : [];
  var isMobile = window.innerWidth <= 768;
  var ticking = false;
  var activeChapter = null;
  var headerHeight = 0;
  var orbitPaused = false;
  var explainerCompleted = false;

  /* ----------------------------------------------------------
     Measure site header height for sticky offset (embedded mode)
     ---------------------------------------------------------- */
  function measureHeaderHeight() {
    if (!isEmbedded) { headerHeight = 0; return; }
    var siteHeader = document.querySelector('.site-header');
    if (siteHeader) {
      headerHeight = siteHeader.offsetHeight;
    } else {
      // Fallback: check sticky wrapper
      var wrapper = document.querySelector('.header-sticky-wrapper');
      headerHeight = wrapper ? wrapper.offsetHeight : 0;
    }
    // Set CSS custom property for sticky calculations
    page.style.setProperty('--fe-header-h', headerHeight + 'px');
  }

  /* ----------------------------------------------------------
     Camera choreography keyframes per format.
     Each entry: { p: scroll progress 0-1, theta: deg, phi: deg, r: radius multiplier }
     theta = azimuthal (horizontal orbit), phi = polar (vertical), r = distance
     ---------------------------------------------------------- */
  /* ----------------------------------------------------------
     Debug mode: add ?debug to URL to show orbit values overlay.
     Useful for tuning choreography keyframes in real-time.
     ---------------------------------------------------------- */
  var debugMode = window.location.search.indexOf('debug') !== -1;
  var debugEl = null;
  if (debugMode) {
    debugEl = document.createElement('div');
    debugEl.className = 'fe-debug';
    debugEl.textContent = 'FE debug';
    document.body.appendChild(debugEl);
  }

  var choreography = {
    dibond: [
      // Clockwise 360°, dramatic corner close-up early, then pulls back
      { p: 0.00, theta: 0,    phi: 75, r: 1.0  },
      { p: 0.10, theta: 40,   phi: 70, r: 0.55 },
      { p: 0.20, theta: 55,   phi: 68, r: 0.50 },
      { p: 0.35, theta: 120,  phi: 65, r: 0.80 },
      { p: 0.50, theta: 180,  phi: 60, r: 0.82 },
      { p: 0.70, theta: 270,  phi: 68, r: 0.88 },
      { p: 1.00, theta: 360,  phi: 75, r: 1.0  }
    ],
    canvas: [
      // Clockwise 360°, deeper vertical dip at back
      { p: 0.00, theta: 0,    phi: 75, r: 1.0  },
      { p: 0.15, theta: 60,   phi: 72, r: 0.95 },
      { p: 0.30, theta: 120,  phi: 66, r: 0.88 },
      { p: 0.45, theta: 180,  phi: 60, r: 0.82 },
      { p: 0.60, theta: 240,  phi: 66, r: 0.85 },
      { p: 0.75, theta: 300,  phi: 72, r: 0.92 },
      { p: 1.00, theta: 360,  phi: 75, r: 1.0  }
    ],
    framed: [
      // Clockwise 360°, starts wide and pulls in at back
      { p: 0.00, theta: 0,    phi: 75, r: 1.1  },
      { p: 0.15, theta: 60,   phi: 72, r: 1.0  },
      { p: 0.30, theta: 120,  phi: 68, r: 0.85 },
      { p: 0.45, theta: 180,  phi: 65, r: 0.75 },
      { p: 0.60, theta: 240,  phi: 68, r: 0.85 },
      { p: 0.75, theta: 300,  phi: 72, r: 1.0  },
      { p: 1.00, theta: 360,  phi: 75, r: 1.1  }
    ],
    unframed: [
      // Clockwise 360°, gentle vertical sway, same starting angle as others
      { p: 0.00, theta: 0,    phi: 75, r: 1.0  },
      { p: 0.15, theta: 60,   phi: 72, r: 0.95 },
      { p: 0.30, theta: 120,  phi: 68, r: 0.90 },
      { p: 0.45, theta: 180,  phi: 65, r: 0.85 },
      { p: 0.60, theta: 240,  phi: 68, r: 0.88 },
      { p: 0.75, theta: 300,  phi: 72, r: 0.95 },
      { p: 1.00, theta: 360,  phi: 75, r: 1.0  }
    ]
  };

  /* ----------------------------------------------------------
     Interpolation helpers
     ---------------------------------------------------------- */
  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function interpolateOrbit(keyframes, progress) {
    var p = Math.max(0, Math.min(1, progress));
    var lo = keyframes[0];
    var hi = keyframes[keyframes.length - 1];
    for (var i = 0; i < keyframes.length - 1; i++) {
      if (p >= keyframes[i].p && p <= keyframes[i + 1].p) {
        lo = keyframes[i];
        hi = keyframes[i + 1];
        break;
      }
    }
    var range = hi.p - lo.p;
    var t = range > 0 ? (p - lo.p) / range : 0;
    return {
      theta: lerp(lo.theta, hi.theta, t),
      phi: lerp(lo.phi, hi.phi, t),
      r: lerp(lo.r, hi.r, t)
    };
  }

  /* ----------------------------------------------------------
     Chapter scroll progress (0→1 as chapter scrolls through)
     ---------------------------------------------------------- */
  function getChapterProgress(el) {
    var rect = el.getBoundingClientRect();
    var total = el.offsetHeight - window.innerHeight;
    if (total <= 0) return 0;
    var scrolled = -rect.top;
    return Math.max(0, Math.min(1, scrolled / total));
  }

  /* ----------------------------------------------------------
     Update model-viewer camera orbit.
     Cache each model's initial radius so the r multiplier
     is always applied against a stable base (not compounding).
     ---------------------------------------------------------- */
  var baseRadii = {};

  function getBaseRadius(mv, name) {
    if (baseRadii[name] !== undefined) return baseRadii[name];
    try {
      var orbit = mv.getCameraOrbit();
      if (orbit && typeof orbit.radius === 'number' && orbit.radius > 0) {
        baseRadii[name] = orbit.radius;
        return orbit.radius;
      }
    } catch (e) { /* not ready yet */ }
    return null;
  }

  function applyCameraOrbit(mv, orbit, name) {
    if (!mv) return;
    var base = getBaseRadius(mv, name);
    if (base !== null) {
      mv.cameraOrbit = orbit.theta + 'deg ' + orbit.phi + 'deg ' + (base * orbit.r) + 'm';
    } else {
      mv.cameraOrbit = orbit.theta + 'deg ' + orbit.phi + 'deg auto';
    }
  }

  /* ----------------------------------------------------------
     Nav indicator positioning
     ---------------------------------------------------------- */
  function updateNavIndicator(tab) {
    if (!indicator || !tab) return;
    indicator.style.left = tab.offsetLeft + 'px';
    indicator.style.width = tab.offsetWidth + 'px';
  }

  function setActiveNav(chapterName) {
    navTabs.forEach(function (tab) {
      var isActive = tab.getAttribute('data-fe-nav') === chapterName;
      tab.classList.toggle('is-active', isActive);
      if (isActive) updateNavIndicator(tab);
    });
  }

  /* ----------------------------------------------------------
     Callout visibility based on scroll progress
     ---------------------------------------------------------- */
  function updateCallouts(chapter, progress) {
    var callouts = chapter.querySelectorAll('.fe-callout');
    callouts.forEach(function (callout) {
      var threshold = parseFloat(callout.getAttribute('data-fe-progress') || 0);
      var fadeOut = threshold + 0.45;
      var visible = progress >= threshold && progress < fadeOut;
      callout.classList.toggle('is-visible', visible);
    });
  }

  /* ----------------------------------------------------------
     Main scroll handler
     ---------------------------------------------------------- */
  function onScroll() {
    if (ticking) return;
    ticking = true;

    requestAnimationFrame(function () {
      ticking = false;

      // Nav shadow — different threshold when embedded (section is further down the page)
      if (nav) {
        var navRect = nav.getBoundingClientRect();
        var stuckToTop = navRect.top <= headerHeight + 2;
        nav.classList.toggle('is-scrolled', stuckToTop);
      }

      chapters.forEach(function (chapter) {
        var rect = chapter.getBoundingClientRect();
        var inView = rect.top < window.innerHeight && rect.bottom > 0;
        if (!inView) return;

        var name = chapter.getAttribute('data-fe-chapter');
        var progress = getChapterProgress(chapter);

        // Activate chapter in nav when it occupies the viewport
        if (rect.top < window.innerHeight * 0.5 && rect.bottom > window.innerHeight * 0.3) {
          if (activeChapter !== name) {
            activeChapter = name;
            setActiveNav(name);
          }
        }

        // Camera orbit (skip when tuner has paused)
        if (!orbitPaused) {
          var mv = chapter.querySelector('.fe-model');
          var keys = choreography[name];
          if (mv && keys) {
            var orbit = interpolateOrbit(keys, progress);
            applyCameraOrbit(mv, orbit, name);

            // Debug overlay
            if (debugMode && debugEl && activeChapter === name) {
              debugEl.textContent = name + ' | p: ' + progress.toFixed(2) +
                ' | θ: ' + orbit.theta.toFixed(1) + '° φ: ' + orbit.phi.toFixed(1) +
                '° r: ' + orbit.r.toFixed(2);
            }
          }
        }

        updateCallouts(chapter, progress);

        // Track completion when last chapter is scrolled past 90%
        if (!explainerCompleted && progress > 0.9 && chapter === chapters[chapters.length - 1]) {
          explainerCompleted = true;
          if (typeof gtag !== 'undefined') gtag('event', 'format_explainer_completed');
        }
      });
    });
  }

  /* ----------------------------------------------------------
     Loader overlay: show progress %, hide on load
     ---------------------------------------------------------- */
  function setupLoaders() {
    chapters.forEach(function (chapter) {
      var mv = chapter.querySelector('.fe-model');
      var loader = chapter.querySelector('[data-fe-loader]');
      if (!mv || !loader) return;

      var pctEl = loader.querySelector('.fe-loader__pct');

      mv.addEventListener('progress', function (e) {
        if (pctEl && e.detail && typeof e.detail.totalProgress === 'number') {
          var pct = Math.round(e.detail.totalProgress * 100);
          pctEl.textContent = 'Loading 3D model \u00b7 ' + pct + '%';
        }
      });

      mv.addEventListener('load', function () {
        loader.classList.add('is-loaded');
        setTimeout(function () { loader.style.display = 'none'; }, 500);
      });
    });
  }

  /* ----------------------------------------------------------
     Lazy loading: only render model-viewers in/near viewport
     ---------------------------------------------------------- */
  function setupLazyModels() {
    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var mv = entry.target.querySelector('.fe-model');
        if (!mv) return;
        if (entry.isIntersecting && mv.dismissPoster) {
          mv.dismissPoster();
        }
      });
    }, {
      rootMargin: '200px 0px',
      threshold: 0
    });

    chapters.forEach(function (chapter) {
      observer.observe(chapter);
    });
  }

  /* ----------------------------------------------------------
     Nav tab click → smooth scroll (accounting for header offset)
     ---------------------------------------------------------- */
  function setupNavClicks() {
    navTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var target = document.getElementById('fe-' + tab.getAttribute('data-fe-nav'));
        if (target) {
          var offset = (nav ? nav.offsetHeight : 0) + headerHeight;
          var top = target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
      });
    });
  }

  /* ----------------------------------------------------------
     Resize handler
     ---------------------------------------------------------- */
  function onResize() {
    isMobile = window.innerWidth <= 768;
    measureHeaderHeight();
    if (activeChapter) setActiveNav(activeChapter);
  }

  /* ----------------------------------------------------------
     Init
     ---------------------------------------------------------- */
  function init() {
    measureHeaderHeight();
    setupNavClicks();
    setupLoaders();
    setupLazyModels();

    if (navTabs.length > 0) {
      navTabs[0].classList.add('is-active');
      activeChapter = navTabs[0].getAttribute('data-fe-nav');
      requestAnimationFrame(function () {
        updateNavIndicator(navTabs[0]);
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    onScroll();
  }

  /* ----------------------------------------------------------
     Tuner API — exposed on window for debug/tuner panel
     ---------------------------------------------------------- */
  if (debugMode) {
    window.__FE_TUNER_READY = {
      choreography: choreography,
      getActiveChapter: function () { return activeChapter; },
      getProgress: function (name) {
        if (!name) return 0;
        var el = document.getElementById('fe-' + name);
        return el ? getChapterProgress(el) : 0;
      },
      getBaseRadius: function (name) {
        return name && baseRadii[name] !== undefined ? baseRadii[name] : null;
      },
      setPaused: function (val) { orbitPaused = !!val; },
      applyOrbitAtProgress: function (name, progress) {
        var keys = choreography[name];
        if (!keys) return;
        var el = document.getElementById('fe-' + name);
        if (!el) return;
        var mv = el.querySelector('.fe-model');
        if (!mv) return;
        var orbit = interpolateOrbit(keys, progress);
        applyCameraOrbit(mv, orbit, name);
      }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
